import React, { useEffect, useState } from 'react';
import { Toolbar, ToolbarItem } from '../common/components/Toolbar';
import { Searchbar } from '../common/components/Searchbar';
import { Button, Select } from '../common';
import { Icon } from '../common/components/Icon';
import DateRangePicker from '../common/DateRangePicker';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import { ButtonGroup } from '../common/components/ButtonGroup';
import styled from 'styled-components';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import OnPremPORequestObjectOptimized from '../../models/entities/OnPremPORequestObjectOptimized';
import Memoized from '../common/Memoized';
import Filter from '../common/tables/Filter';
import { useNavigate } from 'react-router-dom';

export type PORFilters = {
	status: CheckboxOption[];
	scope: CheckboxOption[];
	type: CheckboxOption[];
	searchUser: string;
	orderBy: string;
	fromDate: string;
	toDate: string;
};

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

export type OnPremObjectsMemoizedData = {
	searchUser: void;
	status: void;
	scope: void;
	type: void;
	orderBy: void;
	fromDate: void;
	toDate: void;
};

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	type: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined,
} as OnPremObjectsMemoizedData;

const memoized = new Memoized(defaultMemoizedDataStructure);

interface OnPremPORequestObjectsHomeFiltersProps {
	requests: OnPremPORequestObjectOptimized[];
	setFilteredRequests: (requests: OnPremPORequestObjectOptimized[]) => void;
	loading: boolean;
}

const OnPremPORequestObjectsHomeFilters: React.FC<OnPremPORequestObjectsHomeFiltersProps> = ({
	requests,
	setFilteredRequests,
	loading,
}: OnPremPORequestObjectsHomeFiltersProps) => {
	const navigate = useNavigate();

	const getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			status: [
				{ label: 'Submitted', value: 'Submitted', checked: true },
				{ label: 'Rejected', value: 'Rejected', checked: true },
				{ label: 'Draft', value: 'Draft', checked: true },
				{ label: 'Pending for Policy Team Approval', value: 'Pending for Policy Team Approval', checked: true },
				{
					label: 'Pending Network Policy Solution Architecture approval',
					value: 'Pending Network Policy Solution Architecture approval',
					checked: true,
				},
				{
					label: 'Pending Operations Technology Cybersecurity Risk Management approval',
					value: 'Pending Operations Technology Cybersecurity Risk Management approval',
					checked: true,
				},
				{ label: 'Pending for Cyber Team Approval', value: 'Pending for Cyber Team Approval', checked: true },
				{
					label: 'Pending Network Connection Owner approval',
					value: 'Pending Network Connection Owner approval',
					checked: true,
				},
				{
					label: 'Pending Application Owner approval',
					value: 'Pending Application Owner approval',
					checked: true,
				},
				{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
				{ label: 'Completed', value: 'Completed', checked: true },
				{ label: 'Cancelled', value: 'Cancelled', checked: true },
				{ label: 'Pending Proxy Architect approval', value: 'Pending Proxy Architect approval', checked: true },
				{ label: 'Pending Business DOAG Approval', value: 'Pending Business DOAG Approval', checked: true },
				{ label: 'Review', value: 'Review', checked: true },
				{ label: 'Implementation Failed', value: 'Implementation Failed', checked: false },
				{ label: 'Error', value: 'Error', checked: false },
			],
			type: [
				{ label: 'L4 -> Internet', value: OnPremPortOpeningRequestsTypes.InternetDMZ.name, checked: true },
				{
					label: 'Internal -> Certified Connection',
					value: OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
					checked: true,
				},
			],
			scope: [
				{
					label: PortOpeningRequestsDirection.L4ToInternet.displayName,
					value: PortOpeningRequestsDirection.L4ToInternet.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.iBossProxyToInternet.displayName,
					value: PortOpeningRequestsDirection.iBossProxyToInternet.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.InternetToNorthFirewall.displayName,
					value: PortOpeningRequestsDirection.InternetToNorthFirewall.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.InternetToSouthFirewall.displayName,
					value: PortOpeningRequestsDirection.InternetToSouthFirewall.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.L4ToL3LablanXTO.displayName,
					value: PortOpeningRequestsDirection.L4ToL3LablanXTO.name,
					checked: true,
				},
			],
			orderBy: 'newest',
			fromDate: undefined,
			toDate: undefined,
		} as PORFilters;

		return defaultFilters;
	};

	const getFilters = () => {
		const defaultFilters = getDefaultFilters();
		const filters = {
			searchUser: memoized.getMemoizedData('searchUser', defaultFilters.searchUser),
			status: memoized.getMemoizedData('status', defaultFilters.status),
			type: memoized.getMemoizedData('type', defaultFilters.type),
			scope: memoized.getMemoizedData('scope', defaultFilters.scope),
			orderBy: memoized.getMemoizedData('orderBy', defaultFilters.orderBy),
			fromDate: memoized.getMemoizedData('fromDate', defaultFilters.fromDate),
			toDate: memoized.getMemoizedData('toDate', defaultFilters.toDate),
		} as PORFilters;

		return filters;
	};

	const allFiltersChecked = (key: string) => {
		return getFilters()[key].reduce((acc, box) => acc && box.checked, true);
	};

	const saveFilters = () => {
		memoized.saveMemoizeData('status', filter.filters.status);
		memoized.saveMemoizeData('orderBy', filter.filters.orderBy);
		memoized.saveMemoizeData('type', filter.filters.type);
		memoized.saveMemoizeData('scope', filter.filters.scope);
		memoized.saveMemoizeData('searchUser', filter.filters.searchUser);
		memoized.saveMemoizeData('fromDate', filter.filters.fromDate);
		memoized.saveMemoizeData('toDate', filter.filters.toDate);
	};

	const filterPORs = () => {
		let filteredRequests = [];

		saveFilters();

		const filterCallback = (por: OnPremPORequestObjectOptimized) => {
			return (
				filter.filterCheckBox(
					'status',
					por.onPremWorkflowInstance.currentWorkflowInstanceState.onPremWorkflowState?.name
				) &&
				filter.filterCheckBox('type', por.type?.name) &&
				filter.filterCheckBox('scope', por.direction?.name) &&
				filter.isInDateRange('fromDate', 'toDate', new Date(por.onPremWorkflowInstance.createdDate)) &&
				filter.filterSearchText('searchUser', por.onPremWorkflowInstance.createdBy.displayName)
			);
		};

		filteredRequests = requests.filter(filterCallback).sort((a, b) => {
			if (filter.filters.orderBy == undefined || filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}
			return a.id > b.id ? 1 : -1;
		});

		setFilteredRequests(filteredRequests);
	};

	useEffect(() => {
		filter.updateFilterFunction(filterPORs);
		filter.filter();
	}, [requests]);

	useEffect(() => {
		filter.filter();
	}, []);

	const [filter] = useState<Filter<PORFilters>>(new Filter(filterPORs, getFilters()));

	const goToNewRequest = () => {
		navigate('/onPremportOpeningObjects/new');
	};

	const clearFilters = () => {
		memoized.resetAllMemoizeData();
		filter.updateFilters(getFilters());
	};

	return (
		<>
			{filter && (
				<>
					<Toolbar>
						<ToolbarItem stretch>
							<Searchbar
								condensed
								placeholder="Search"
								value={filter.filters.searchUser}
								onChange={(e) => filter.handleSearchTextChange(e, 'searchUser')}
							/>
						</ToolbarItem>
						<ToolbarItem right>
							<Button
								variant="primary"
								size="small"
								isLoading={loading}
								onClick={() => {
									goToNewRequest();
								}}
							>
								<Icon size="small" icon="plus-filled" />
								<span>New Request</span>
							</Button>
						</ToolbarItem>
					</Toolbar>

					<Toolbar>
						<ToolbarItem>
							<DateRangePicker
								toDateOnChangeFunction={(e) => filter.handleSearchTextChange(e, 'toDate')}
								fromDateOnChangeFunction={(e) => filter.handleSearchTextChange(e, 'fromDate')}
								fromDateValue={filter.filters.fromDate}
								toDateValue={filter.filters.toDate}
								containerStyle={{ position: 'relative', bottom: '8px' }}
								pickerStyle={{ margin: '0px' }}
							/>
						</ToolbarItem>
						<ToolbarItem>
							<CheckboxFilter
								label="Type"
								buttonProps={{ size: 'small' }}
								onChange={(event) => filter.handleCheckboxChange(event, 'type')}
								options={filter.filters.type}
								allDefaultChecked={allFiltersChecked('type')}
							/>
						</ToolbarItem>
						<ToolbarItem>
							<CheckboxFilter
								label="Scope"
								buttonProps={{ size: 'small' }}
								onChange={(event) => filter.handleCheckboxChange(event, 'scope')}
								options={filter.filters.scope}
								allDefaultChecked={allFiltersChecked('scope')}
							/>
						</ToolbarItem>
						<ToolbarItem>
							<CheckboxFilter
								label="Status"
								onChange={(event) => filter.handleCheckboxChange(event, 'status')}
								options={filter.filters.status}
								allDefaultChecked={allFiltersChecked('status')}
							/>
						</ToolbarItem>
						<ToolbarItem>
							<MyLink onClick={() => clearFilters()}>Clear filters</MyLink>
						</ToolbarItem>
						<ToolbarItem right>
							<ButtonGroup>
								<MyP>Sort by</MyP>
								<Select
									value={filter.filters.orderBy}
									onChange={(e) => filter.handleSearchTextChange(e, 'orderBy')}
									options={[
										{ text: 'Newest', value: 'newest' },
										{ text: 'Oldest', value: 'oldest' },
									]}
								/>
							</ButtonGroup>
						</ToolbarItem>
					</Toolbar>
				</>
			)}
		</>
	);
};

export default OnPremPORequestObjectsHomeFilters;
